/**
 * Annual I&G Review reporting: the talent dashboard and its Excel exports.
 *
 * Three reads, and every view and every sheet comes from them, so a view and an
 * export cannot disagree (performance-service app/api/talent_reporting.py):
 *   GET /performance/talent/reporting/people    everyone in scope, person by person
 *   GET /performance/talent/reporting/sessions  the review's sessions
 *   GET /performance/talent/reporting/actions   what the rooms recorded
 *
 * What the review concluded is what the chairs published when they completed their
 * sessions. Administrators and HR administrators only (Victor, 9 Oct 2026).
 */

import { API_CONFIG } from '@/config/api'
import { authenticatedFetch } from '../authenticatedFetch'

export type ReportingLevel = 'high' | 'medium' | 'low'
export type ReportingReadiness = 'ready-now' | 'ready-1-year' | 'ready-2-plus'

/**
 * Where a person in scope stands:
 *   placed         on the year's published grid
 *   not_placed     handed in under the rule before 22 Sep 2026, kept off the grid
 *   session_open   on a list in a session that has not completed
 *   not_on_a_list  on no list in any session this year
 */
export type PlacementStatus = 'placed' | 'not_placed' | 'session_open' | 'not_on_a_list'

/** Error carrying the server's own status and refusal text. */
export interface TalentReportingApiError extends Error {
  status: number
  detail: string
}

export interface ReportingRating {
  review_year: number
  review_type: string
  rating: string
  calibrated?: boolean
  source?: string
  label?: string | null
}

export interface ReportingSuccessorRole {
  role: string
  readiness: ReportingReadiness | string
  incumbent_id: string | null
}

export interface ReportingPerson {
  employee_id: string
  name: string
  job_title: string | null
  /** The Role Master role their seat links to. */
  role_title: string | null
  role_code: string | null
  function: string | null
  department: string | null
  team_1: string | null
  team_2: string | null
  team_3: string | null
  campus: string | null
  grade: string | null
  /** Role Master's position level: Manage Business down to Manage Self. */
  leadership_layer: string | null
  /** Flagged critical in Role Master; null when the seat has no Role Master link. */
  critical_role: boolean | null
  employment_category: string | null
  position_type: string | null
  gender: string | null
  race: string | null
  nationality: string | null
  original_hire_date: string | null
  tenure_years: number | null
  leader_id: string | null
  leader_name: string | null
  one_up_name: string | null
  /** The year's mid-year Impact & Growth Indicator. */
  midyear_rating: string | null
  rating_history: ReportingRating[]
  placement_status: PlacementStatus
  not_placed_reason: string | null
  session_id: string | null
  session_name: string | null
  session_date: string | null
  chair_name: string | null
  performance: ReportingLevel | null
  potential: ReportingLevel | null
  block: number | null
  is_hipo: boolean
  hipo_category: string | null
  next_role: string | null
  next_role_readiness: ReportingReadiness | null
  rationale: string | null
  position_changed: boolean
  key_person: boolean
  key_person_reason: string | null
  retention_risk: string | null
  mitigations: string | null
  has_growth_plan: boolean
  growth_plan_complete: boolean
  mobility: 'yes' | 'maybe' | 'no' | null
  growth_focus: string | null
  plan_next_roles: { title: string; readiness: ReportingReadiness | null }[]
  is_successor: boolean
  successor_for: ReportingSuccessorRole[]
  /** Successors named for this person's own role, by readiness. */
  own_role_successors: Record<ReportingReadiness, number>
}

export interface ReportingSessionCounts {
  total: number
  completed: number
  open: number
}

/** Who the server says is reading: an administrator sees everything; a holder of
 *  the talent:dashboard grant sees their areas ('Academics', or
 *  'Academics › Faculty'; none listed = the whole organisation), with nobody's
 *  gender or race and no downloads. */
export interface ReportingViewer {
  admin: boolean
  areas: string[]
}

export interface ReportingPeopleResponse {
  review_year: number
  generated_at: string
  /** Absent from a server older than the grant: only administrators could read it then. */
  viewer?: ReportingViewer
  sessions: ReportingSessionCounts
  people: ReportingPerson[]
}

export interface ReportingSession {
  id: string
  name: string | null
  date: string | null
  status: string
  chair_id: string | null
  chair_name: string | null
  chair_function: string | null
  chair_department: string | null
  people_leaders: string[]
  hr: string[]
  observers: string[]
  lists_in: number
  lists_total: number
  placements_in: number
  placements_total: number
  placements_outstanding: number
  completed_at: string | null
}

export interface ReportingSessionsResponse {
  review_year: number
  generated_at: string
  sessions: ReportingSession[]
}

export interface ReportingAction {
  session_id: string
  session_name: string | null
  session_date: string | null
  chair_name: string | null
  employee_id: string
  employee_name: string | null
  kind: string
  what: string
  summary: string | null
  from_block: number | null
  to_block: number | null
  growth_plan_item: string | null
  role: string | null
  readiness: string | null
  responsible_id: string | null
  responsible_name: string | null
  due_date: string | null
  recorded_by: string | null
  recorded_at: string | null
}

export interface ReportingActionsResponse {
  review_year: number
  generated_at: string
  actions: ReportingAction[]
}

/** A Role Master role and its approved positions (identity's seat rule: an empty
 *  incumbent alone is not a vacancy). Seats with no Role Master link group under
 *  their own title, `linked` false. Contractor and casual seats are left out. */
export interface ReportingRole {
  role_code: string | null
  role_title: string
  linked: boolean
  function: string | null
  department: string | null
  team_1: string | null
  team_2: string | null
  team_3: string | null
  leadership_layer: string | null
  grade: string | null
  /** Flagged critical in Role Master; null when the seats have no Role Master link. */
  critical: boolean | null
  positions: number
  filled: number
  vacant: number
}

/** A published succession plan: a People Leader's own role and its successors. */
export interface ReportingSuccessionPlan {
  incumbent_id: string | null
  /** The Role Master role the incumbent holds; null when it cannot be placed. */
  role_code: string | null
  role_title: string
  /** The role as it was nominated for. */
  plan_role_name: string
  successors: { candidate_id: string; readiness: ReportingReadiness | string }[]
}

export interface ReportingRolesResponse {
  review_year: number
  generated_at: string
  viewer?: ReportingViewer
  /** The last completed F&O positions sync behind the counts. */
  positions_synced_at: string | null
  roles: ReportingRole[]
  succession: ReportingSuccessionPlan[]
}

class TalentReportingService {
  private baseURL = API_CONFIG.baseURL

  private async request<T>(endpoint: string): Promise<T> {
    const response = await authenticatedFetch(`${this.baseURL}${endpoint}`, {
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      const raw = await response.text()
      let detail = raw
      try {
        const parsed = JSON.parse(raw)
        if (typeof parsed?.detail === 'string') detail = parsed.detail
      } catch {
        /* not JSON: keep the raw body */
      }
      const err = new Error(detail || `Request failed (${response.status})`) as TalentReportingApiError
      err.status = response.status
      err.detail = detail
      throw err
    }
    return response.json()
  }

  getPeople(reviewYear: number): Promise<ReportingPeopleResponse> {
    return this.request(`/performance/talent/reporting/people?review_year=${reviewYear}`)
  }

  getSessions(reviewYear: number): Promise<ReportingSessionsResponse> {
    return this.request(`/performance/talent/reporting/sessions?review_year=${reviewYear}`)
  }

  getActions(reviewYear: number): Promise<ReportingActionsResponse> {
    return this.request(`/performance/talent/reporting/actions?review_year=${reviewYear}`)
  }

  getRoles(reviewYear: number): Promise<ReportingRolesResponse> {
    return this.request(`/performance/talent/reporting/roles?review_year=${reviewYear}`)
  }
}

export const talentReportingService = new TalentReportingService()
export default talentReportingService
